import { useEffect, useRef, type ReactNode } from 'react';
import {
  TabsComponent,
  Field,
  RadioCard,
  Text,
} from '@noorden-insurtech/alba-design-system';
import { Layers, LayoutGrid } from 'lucide-react';
import type { PerfilDoGrupo } from '@/features/quotes/schemas/capital-global';
import { formatCurrency } from '@/shared/utils/currencyFormatter';
import type { EntityTab } from '../../types';
import { useHorizontalDragScroll } from '../hooks/use-horizontal-drag-scroll.hook';

// ─── EntityRiskTabs ─────────────────────────────────────────────────────────

type EntityRiskTabsProps = {
  entities: EntityTab[];
  activeEntityId: string;
  onTabChange: (entityId: string) => void;
  children: ReactNode;
};

export function EntityRiskTabs({
  entities,
  activeEntityId,
  onTabChange,
  children,
}: EntityRiskTabsProps) {
  const listRef = useRef<HTMLDivElement>(null);

  useHorizontalDragScroll({ containerRef: listRef });

  useEffect(() => {
    if (!activeEntityId || !listRef.current) return;

    const activeTab = listRef.current.querySelector<HTMLElement>(
      '[data-role="tab"][data-selected]'
    );

    activeTab?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'nearest',
    });
  }, [activeEntityId]);

  return (
    <TabsComponent.Root
      value={activeEntityId}
      onValueChange={({ value }) => onTabChange(value)}
    >
      <TabsComponent.List
        ref={listRef}
        className='flex w-full cursor-grab flex-nowrap overflow-x-auto touch-pan-x select-none border-b border-alba-blue-500 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden'
      >
        {entities.map(entity => (
          <TabsComponent.Trigger
            key={entity.id}
            value={entity.id}
            className='shrink-0 px-12 py-3 text-sm bg-transparent text-alba-gray-500 data-selected:bg-transparent data-selected:font-bold data-selected:text-alba-blue-500 hover:bg-transparent hover:text-alba-blue-500'
          >
            {entity.label}
          </TabsComponent.Trigger>
        ))}
        <TabsComponent.Indicator className='h-[3px] w-[var(--width)] translate-x-0 rounded-tl-none rounded-tr-none bg-alba-blue-500' />
      </TabsComponent.List>

      <TabsComponent.Content value={activeEntityId} className='pt-6'>
        {children}
      </TabsComponent.Content>
    </TabsComponent.Root>
  );
}

// ─── PerfilDoGrupoSection ────────────────────────────────────────────────────

type PerfilDoGrupoProps = {
  value: PerfilDoGrupo;
  onChange: (value: PerfilDoGrupo) => void;
  disabled?: boolean;
};

export function PerfilDoGrupoSection({
  value,
  onChange,
  disabled,
}: PerfilDoGrupoProps) {
  return (
    <div className='flex flex-col gap-4'>
      <Text className='text-lg font-bold text-alba-black-500'>
        Perfil do Grupo
      </Text>

      <RadioCard.Root
        value={value}
        onValueChange={details => onChange(details.value as PerfilDoGrupo)}
        disabled={disabled}
      >
        <div className='flex gap-2'>
          <RadioCard.Item value='ESCALONADO'>
            <RadioCard.ItemControl className='flex-row items-center gap-2'>
              <Layers className='w-6 h-6' strokeWidth={1.6} />
              <RadioCard.ItemText>Escalonado</RadioCard.ItemText>
            </RadioCard.ItemControl>
            <RadioCard.ItemHiddenInput />
          </RadioCard.Item>

          <RadioCard.Item value='UNIFORME'>
            <RadioCard.ItemControl className='flex-row items-center gap-2'>
              <LayoutGrid className='w-6 h-6' strokeWidth={1.6} />
              <RadioCard.ItemText>Uniforme</RadioCard.ItemText>
            </RadioCard.ItemControl>
            <RadioCard.ItemHiddenInput />
          </RadioCard.Item>
        </div>
      </RadioCard.Root>
    </div>
  );
}

// ─── RiskSummaryDisplay ──────────────────────────────────────────────────────

type RiskSummaryDisplayProps = {
  capitalTotalSegurado: number;
  /** Soma de vidas de TODOS os estipulantes, não só da aba ativa. */
  totalLives: number;
  maxTotalLives: number;
};

export function RiskSummaryDisplay({
  capitalTotalSegurado,
  totalLives,
  maxTotalLives,
}: RiskSummaryDisplayProps) {
  const hasExceededLives = totalLives > maxTotalLives;

  return (
    <div className='grid grid-cols-1 lg:grid-cols-2 gap-10 items-end'>
      <Field.Root>
        <Field.Label className='text-sm font-bold text-alba-black-500'>
          Capital Total Segurado
        </Field.Label>
        <Field.Input
          value={
            capitalTotalSegurado > 0 ? formatCurrency(capitalTotalSegurado) : ''
          }
          readOnly
          disabled
          placeholder=''
        />
        {/*
          Contador consolidado: o teto de vidas é sobre a SOMA das abas, então o
          corretor precisa enxergar quanto já foi gasto antes de estourar — o
          campo de uma aba isolada nunca conta a história inteira.
        */}
        <Text
          className={`text-sm ${
            hasExceededLives
              ? 'font-bold text-alba-error-red-500'
              : 'text-alba-gray-600'
          }`}
        >
          Vidas (todos os estipulantes): {totalLives.toLocaleString('pt-BR')} /{' '}
          {maxTotalLives.toLocaleString('pt-BR')}
        </Text>
      </Field.Root>

      <div className='grid grid-cols-2 gap-4 pb-2'>
        <div className='flex flex-col gap-2'>
          <Text className='text-sm font-bold text-alba-black-500'>Custeio</Text>
          <Text className='text-base text-alba-gray-600'>
            Não contributário
          </Text>
        </div>
        <div className='flex flex-col gap-2'>
          <Text className='text-sm font-bold text-alba-black-500'>Adesão</Text>
          <Text className='text-base text-alba-gray-600'>Compulsória</Text>
        </div>
      </div>
    </div>
  );
}
